import { useMemo } from "react";
import { ArrowLeft, ChevronRight, FilePenLine } from "lucide-react";
import { Link, Navigate, useParams } from "react-router-dom";

import Loading from "../components/common/LoaderCommon";
import {
  type WarrantyClaimApi,
  type WarrantyDetailResponse,
  type WarrantyProductApi,
  useGetWarrantyDetail,
} from "../service/warrantyService";
import { Button } from "../utils/components/ui/button";
import type {
  ClaimStatus,
  WarrantyProduct,
  WarrantyCoverageStatus,
} from "./warranty/warrantyShared";
import {
  formatWarrantyDate,
  WarrantyProductArtwork,
  WarrantyProductSummary,
} from "./warranty/warrantyShared";

const toNumberValue = (value: unknown) => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string") {
    const parsedValue = Number(value);

    if (Number.isFinite(parsedValue)) {
      return parsedValue;
    }
  }

  return 0;
};

const toClaimStatus = (value: unknown): ClaimStatus | undefined => {
  if (typeof value !== "string") {
    return undefined;
  }

  const normalizedValue = value.trim().toLowerCase();

  if (normalizedValue.includes("open")) {
    return "Open";
  }

  if (
    normalizedValue.includes("closed") ||
    normalizedValue.includes("close") ||
    normalizedValue.includes("resolved")
  ) {
    return "Closed";
  }

  return undefined;
};

const toCoverageStatus = (value: unknown): WarrantyCoverageStatus => {
  if (typeof value === "string" && value.trim().toLowerCase() === "expired") {
    return "Expired";
  }

  return "Active";
};

type WarrantyDetailInvoice = {
  salesId: string;
  invoiceNumber: string;
  customerName: string;
  purchaseDate: string;
  products: DetailWarrantyProduct[];
};

type DetailWarrantyProduct = WarrantyProduct & {
  claim?: NonNullable<WarrantyProduct["claim"]> & { claimId: string };
};

const normalizeClaim = (claim?: WarrantyClaimApi) => {
  if (!claim) {
    return undefined;
  }

  const claimStatus = toClaimStatus(claim.status);

  if (!claimStatus) {
    return undefined;
  }

  return {
    claimId: String(claim.claimId),
    claimDate: claim.claimDate,
    status: claimStatus,
    issueDescription: claim.issueDescription,
    resolutionNotes: claim.resolutionNotes,
    attachmentName: claim.attachment ?? "",
    attachmentSize: "",
  };
};

const normalizeWarrantyProduct = (
  product: WarrantyProductApi,
): DetailWarrantyProduct => {
  return {
    id: String(product.salesProductId),
    name: product.productName,
    brand: product.brandName,
    warrantyStatus: toCoverageStatus(product.warrantyStatus),
    expiryDate: product.warrantyExpiryDate,
    price: toNumberValue(product.sellingPrice),
    originalPrice:
      toNumberValue(product.mrp) || toNumberValue(product.sellingPrice),
    claim: normalizeClaim(product.claims[0]),
  };
};

const normalizeWarrantyInvoice = (
  item?: WarrantyDetailResponse,
): WarrantyDetailInvoice | null => {
  if (!item) {
    return null;
  }

  const products = item.products.map(normalizeWarrantyProduct);

  return {
    salesId: String(item.salesId),
    invoiceNumber: item.invoiceNumber,
    customerName: item.customerName,
    purchaseDate: item.purchaseDate,
    products,
  };
};

function WarrantyDetail() {
  const { salesId } = useParams();

  const { data, isLoading } = useGetWarrantyDetail(salesId);

  const invoice = useMemo(() => {
    return normalizeWarrantyInvoice(data);
  }, [data]);

  if (isLoading) {
    return <Loading />;
  }

  if (!invoice) {
    return <Navigate to="/warranty" replace />;
  }

  return (
    <div className="flex min-h-full flex-col gap-6 pb-4">
      <section className="rounded-[28px] border border-[#DDE7FB] bg-[radial-gradient(circle_at_top_left,rgba(26,94,249,0.12),transparent_28%),linear-gradient(180deg,#FFFFFF_0%,#F5F9FF_100%)] p-6 shadow-[0_18px_50px_-30px_rgba(15,23,42,0.35)] sm:p-8">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div className="space-y-4 space-x-5">
            <Link
              to="/warranty"
              className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 transition hover:text-slate-700"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to warranty register
            </Link>
            <div className="inline-flex items-center rounded-full border border-[#CFE0FF] bg-[#EEF4FF] px-3 py-1 text-xs font-semibold uppercase tracking-[0.24em] text-[#1A5EF9]">
              {invoice.invoiceNumber}
            </div>
            <div>
              <h1 className="text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
                Warranty Detail
              </h1>
              <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-500 sm:text-base">
                Review invoice-linked products, coverage windows, and move
                directly into warranty claims when service action is needed.
              </p>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="rounded-3xl border border-[#E6ECF5] bg-white p-5 shadow-[0_16px_32px_-28px_rgba(15,23,42,0.55)]">
              <p className="text-xs uppercase tracking-[0.2em] text-slate-400">
                Customer Name
              </p>
              <p className="mt-3 text-xl font-semibold text-slate-900">
                {invoice.customerName}
              </p>
            </div>
            <div className="rounded-3xl border border-[#E6ECF5] bg-white p-5 shadow-[0_16px_32px_-28px_rgba(15,23,42,0.55)]">
              <p className="text-xs uppercase tracking-[0.2em] text-slate-400">
                Purchase Date
              </p>
              <p className="mt-3 text-xl font-semibold text-slate-900">
                {formatWarrantyDate(invoice.purchaseDate)}
              </p>
            </div>
          </div>
        </div>
      </section>

      <div className="rounded-[28px] border border-[#DDE7FB] bg-white p-6 shadow-[0_18px_40px_-32px_rgba(15,23,42,0.45)]">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-semibold tracking-tight text-slate-900">
              Product Detail
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              {invoice.products.length} products linked to this invoice.
            </p>
          </div>
        </div>

        <div className="space-y-5">
          {invoice.products.map((product) => (
            <div
              key={product.id}
              className="rounded-[26px] border border-[#DDE7FB] bg-[linear-gradient(180deg,#FFFFFF_0%,#F7FAFF_100%)] p-5 shadow-[0_14px_24px_-28px_rgba(15,23,42,0.9)]"
            >
              <div className="flex flex-col gap-5 md:flex-row md:items-start">
                <WarrantyProductArtwork />
                <div className="flex-1">
                  <WarrantyProductSummary product={product} />
                </div>
                <div className="flex shrink-0 items-start">
                  {product.claim ? (
                    <Button
                      asChild
                      variant="outline"
                      className="rounded-xl border-[#BFD1FF] text-[#1A5EF9] hover:bg-[#EEF4FF] hover:text-[#0E4FDE]"
                    >
                      <Link
                        to={`/warranty/${invoice.salesId}/claim/${product.claim.claimId}`}
                      >
                        View Claim
                        <ChevronRight className="h-4 w-4" />
                      </Link>
                    </Button>
                  ) : (
                    <Button
                      asChild
                      className="rounded-xl bg-[#1A5EF9] text-white hover:bg-[#0E4FDE]"
                    >
                      <Link
                        to={`/warranty/${invoice.salesId}/claim/${product.id}/new`}
                      >
                        Add Warranty Claim
                        <FilePenLine className="h-4 w-4" />
                      </Link>
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default WarrantyDetail;
